/** Local WebRTC playout gives browser AEC a remote-audio reference, including
 * browsers that do not cancel ordinary Web Audio output. No microphone track
 * or external ICE server participates in this loopback. */
export function createEchoPlayback(context: AudioContext) {
  const destination = context.createMediaStreamDestination();
  const sender = new RTCPeerConnection({ iceServers: [] });
  const receiver = new RTCPeerConnection({ iceServers: [] });
  const audio = new Audio();
  audio.autoplay = true;
  let closed = false;
  let rejectPending: ((reason: Error) => void) | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const close = () => {
    closed = true;
    clearTimeout(timeout);
    rejectPending?.(new Error("Enrollment playback stopped."));
    rejectPending = undefined;
    audio.pause();
    audio.srcObject = null;
    sender.close();
    receiver.close();
    destination.stream.getTracks().forEach((track) => track.stop());
    destination.disconnect();
  };
  const start = async () => {
    const playing = new Promise<void>((resolve, reject) => {
      rejectPending = reject;
      timeout = setTimeout(
        () =>
          reject(
            new Error(
              "Couldn't start echo-cancelled playback. Please try again.",
            ),
          ),
        10000,
      );
      receiver.ontrack = (event) => {
        audio.srcObject = new MediaStream([event.track]);
        void audio.play().then(resolve, reject);
      };
    });
    // Attach the rejection handler before negotiating, including cancellation.
    const negotiated = (async () => {
      const pendingCandidates: RTCIceCandidate[] = [];
      const answerCandidates: RTCIceCandidate[] = [];
      sender.onicecandidate = (event) => {
        if (!event.candidate || closed) return;
        if (!receiver.remoteDescription)
          pendingCandidates.push(event.candidate);
        else
          void receiver
            .addIceCandidate(event.candidate)
            .catch(() =>
              rejectPending?.(
                new Error("Couldn't connect enrollment playback."),
              ),
            );
      };
      receiver.onicecandidate = (event) => {
        if (!event.candidate || closed) return;
        if (!sender.remoteDescription) answerCandidates.push(event.candidate);
        else
          void sender
            .addIceCandidate(event.candidate)
            .catch(() =>
              rejectPending?.(
                new Error("Couldn't connect enrollment playback."),
              ),
            );
      };
      destination.stream
        .getTracks()
        .forEach((track) => sender.addTrack(track, destination.stream));
      await sender.setLocalDescription(await sender.createOffer());
      await receiver.setRemoteDescription(sender.localDescription!);
      for (const candidate of pendingCandidates)
        await receiver.addIceCandidate(candidate);
      await receiver.setLocalDescription(await receiver.createAnswer());
      await sender.setRemoteDescription(receiver.localDescription!);
      for (const candidate of answerCandidates)
        await sender.addIceCandidate(candidate);
    })();
    try {
      await Promise.all([playing, negotiated]);
    } finally {
      clearTimeout(timeout);
      rejectPending = undefined;
    }
  };
  return { destination, start, close };
}
